import React, { useEffect, useRef, useState } from 'react';
import MessageErreur, {
  classeChampEnErreur,
} from '../../../../../UI/MessageErreur/MessageErreur';

/**
 * Erreurs des objets du rapport, indexées par chemin (« body_page.1.0 »), et
 * chemin de l'objet en cours d'affichage.
 *
 * Passe par un contexte plutôt que par des props : les listes d'objets sont
 * imbriquées dans les sections, sous-sections et boîtes, et chacun de ces
 * composants aurait sinon dû relayer deux props qu'il n'utilise pas.
 */
export const ContexteErreursObjets = React.createContext({
  erreurs: {},
  chemin: null,
});

/**
 * Éditeur du JSON brut d'un objet.
 *
 * L'ancien champ appelait JSON.parse à chaque frappe : tout texte qui ne
 * formait pas encore du JSON complet levait une exception, la saisie était
 * perdue, et l'on ne pouvait guère que coller un objet entier. Le texte est
 * désormais gardé tel que tapé, transmis dès qu'il se lit comme un objet, et
 * signalé tant qu'il ne se lit pas.
 *
 * @param {object} props
 * @param {object} props.valeur - l'objet affiché
 * @param {(objet: object) => void} props.onValide - reçoit l'objet relu
 * @param {boolean} [props.masquerImage] - remplace l'image en base64, trop
 *   longue pour être éditée, par un renvoi vers l'aperçu
 */
export const EditeurJsonObjet = ({
  valeur,
  onValide,
  masquerImage = false,
  disabled,
  placeholder,
  className,
}) => {
  const versTexte = (objet) =>
    JSON.stringify(
      masquerImage
        ? { ...objet, image_base64: "voir l'aperçu ci-dessus" }
        : objet,
      null,
      2
    );
  const texteDeLaValeur = versTexte(valeur);
  const [texte, setTexte] = useState(texteDeLaValeur);
  const [illisible, setIllisible] = useState(false);
  // Dernier objet transmis, pour ne pas réécrire le texte de l'utilisateur
  // quand le parent renvoie l'objet qu'on vient de lui donner.
  const dernierTransmis = useRef(texteDeLaValeur);

  useEffect(() => {
    if (texteDeLaValeur !== dernierTransmis.current) {
      dernierTransmis.current = texteDeLaValeur;
      setTexte(texteDeLaValeur);
      setIllisible(false);
    }
  }, [texteDeLaValeur]);

  const gererSaisie = (event) => {
    const saisie = event.target.value;
    setTexte(saisie);
    let objet;
    try {
      objet = JSON.parse(saisie);
    } catch (e) {
      setIllisible(true);
      return;
    }
    if (objet === null || typeof objet !== 'object' || Array.isArray(objet)) {
      setIllisible(true);
      return;
    }
    setIllisible(false);
    const relu = masquerImage
      ? { ...objet, image_base64: valeur.image_base64 }
      : objet;
    dernierTransmis.current = versTexte(relu);
    onValide(relu);
  };

  return (
    <>
      <textarea
        className={[className, illisible ? classeChampEnErreur : ''].join(' ')}
        placeholder={placeholder}
        value={texte}
        disabled={disabled}
        onChange={gererSaisie}
      />
      <MessageErreur cle={illisible ? 'format_json_attendu' : null} />
    </>
  );
};
